import { AbstractTailoredWidgetTemplateEditor } from '@/components/template-widgets/AbstractTailoredWidgetEditorTemplate.class';
import { sortPeriodsDesc } from '@/constants/period.constant';
import { AdvizeoInputNumberDirective } from '@/directives/advizeo-input/advizeo-input-number.directive';
import { AdvizeoInputDirective } from '@/directives/advizeo-input/advizeo-input.directive';
import { AdvizeoTooltipDirective } from '@/directives/advizeo-tooltip/advizeo-tooltip.directive';
import { BusinessIndicatorPipe } from '@/features/business-indicators/business-indicator.pipe';
import type { BusinessIntelligenceContextualPeriodCode } from '@/interfaces/BusinessIntelligence.interface';
import { PrettyFeelSharedUiColorName } from '@/shared/ui/theming';
import { UiButtonToggleComponent } from '@/ui/button-toggle/ui-button-toggle.component';
import { type ColorPaletteConfiguration, UiColorPickerComponent } from '@/ui/color-picker/ui-color-picker.component';
import { UiFieldComponent } from '@/ui/field/ui-field';
import { UiIconButtonComponent } from '@/ui/icon-button/ui-icon-button';
import { UiSectionContentComponent } from '@/ui/section/section-content/ui-section-content';
import { UiSectionComponent } from '@/ui/section/ui-section';
import { UiSelectComponent } from '@/ui/select/ui-select';
import { UiToggleComponent } from '@/ui/toggle/ui-toggle';
import { deepCopy } from '@/utils/toolkit/object/copy.utils';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { Component, effect, inject, input, linkedSignal, output, type Signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { filter, of, switchMap } from 'rxjs';
import type { BusinessIndicatorId } from '../../../../interfaces/BusinessIndicator.interface';
import { TranslatePeriodPipe } from '../../../../pipes/translate-period.pipe';
import { BusinessIndicatorsProvider } from '../../../../providers/business-indicator/business-indicators.provider';
import {
    type BandBoundaryColor,
    type BandBoundaryId,
    type BusinessIndicatorGaugeConfig,
    type CustomBand,
    type PeriodOption,
    WidgetsBusinessIndicatorsService
} from '../widgets-business-indicators.service';

@Component({
    selector: 'template-widget-business-indicator-gauge-editor',
    imports: [
        UiFieldComponent,
        AdvizeoInputDirective,
        AdvizeoInputNumberDirective,
        AdvizeoTooltipDirective,
        UiSelectComponent,
        TranslatePipe,
        FormsModule,
        BusinessIndicatorPipe,
        TranslatePeriodPipe,
        UiToggleComponent,
        UiSectionComponent,
        UiSectionContentComponent,
        UiColorPickerComponent,
        UiIconButtonComponent,
        FormsModule,
        UiButtonToggleComponent
    ],
    templateUrl: './template-widget-business-indicator-gauge-editor.component.html',
    styleUrl: './template-widget-business-indicator-gauge-editor.component.scss'
})
export class TemplateWidgetBusinessIndicatorGaugeEditorComponent extends AbstractTailoredWidgetTemplateEditor<BusinessIndicatorGaugeConfig> {
    private _businessIndicatorsOldProvider = inject(BusinessIndicatorsProvider);
    private _widgetsBusinessIndicatorsService = inject(WidgetsBusinessIndicatorsService);
    private _translateService = inject(TranslateService);

    formInputs = input<BusinessIndicatorGaugeConfig>();

    config = output<{
        isValid: boolean;
        formValues: BusinessIndicatorGaugeConfig;
    }>();

    sortPeriodDesc = sortPeriodsDesc;
    periodList: Signal<PeriodOption[]> = this._widgetsBusinessIndicatorsService.getPeriodList();

    businessIndicatorsIds = toSignal(
        this._businessIndicatorsOldProvider
            .getAllowedBusinessIndicatorIds$()
            .pipe(filter(businessIndicator => businessIndicator?.length > 0)),
        { initialValue: [] }
    );

    selectedPeriod = linkedSignal<BusinessIntelligenceContextualPeriodCode | number | null | undefined>(() => {
        return this.formInputs()?.year;
    });

    gaugeName = linkedSignal(() => {
        const formInputs = this.formInputs();
        const selectedBusinessIndicatorId = this.selectedBusinessIndicatorId();

        return (
            formInputs?.gaugeName ||
            (selectedBusinessIndicatorId &&
                this._businessIndicatorsOldProvider.getBusinessIndicatorById(selectedBusinessIndicatorId).name) ||
            ''
        );
    });
    displayLegend = linkedSignal(() => this.formInputs()?.displayLegend ?? true);

    selectedBusinessIndicatorId = linkedSignal<BusinessIndicatorId | null>(() => {
        const businessIndicatorId = this.formInputs()?.businessIndicatorId;
        return businessIndicatorId ? businessIndicatorId : this.businessIndicatorsIds()[0];
    });

    isEstimated = linkedSignal<boolean>(() => {
        const isEstimated = this.formInputs()?.isEstimated;
        return isNotNullish(isEstimated) ?? false;
    });

    selectedBusinessIndicator = toSignal(
        toObservable(this.selectedBusinessIndicatorId).pipe(
            switchMap(businessIndicatorsId =>
                businessIndicatorsId ? this._businessIndicatorsOldProvider.getBusinessIndicatorById$(businessIndicatorsId) : of(undefined)
            )
        )
    );

    bandBoundaryColors = linkedSignal<BandBoundaryColor[]>(() => {
        const formInputs = this.formInputs();
        const bandBoundaryColors = formInputs?.bandBoundary.isCustomBand === false ? formInputs.bandBoundary.bandBoundaryColors : null;
        const businessIndicator = this.selectedBusinessIndicator();
        return bandBoundaryColors
            ? bandBoundaryColors
            : businessIndicator
              ? this._widgetsBusinessIndicatorsService.getBandBoundaryColors(businessIndicator)
              : [];
    });

    customBandBounds = linkedSignal<CustomBand[]>(() => {
        const bandBoundary = this.formInputs()?.bandBoundary;
        if (bandBoundary?.isCustomBand) {
            return bandBoundary.bandBoundaryColors;
        } else {
            return this._widgetsBusinessIndicatorsService.getDefaultBandConfig();
        }
    });

    selectedTabId = linkedSignal<'auto' | 'custom'>(() => ((this.formInputs()?.bandBoundary.isCustomBand ?? false) ? 'custom' : 'auto'));

    constructor() {
        super();

        effect(() => {
            const businessIndicatorId = this.selectedBusinessIndicatorId();
            const period = this.selectedPeriod();
            const isEstimated = this.isEstimated();
            const bandBoundaryColors = this.bandBoundaryColors();
            const gaugeName = this.gaugeName();
            const displayLegend = this.displayLegend();
            const configMode = this.selectedTabId();
            const customBands = this.customBandBounds();

            console.log('period', period);
            if (businessIndicatorId && period) {
                if (configMode === 'auto' && bandBoundaryColors.length > 0) {
                    this.config.emit({
                        isValid: true,
                        formValues: {
                            businessIndicatorId,
                            year: period,
                            isEstimated,
                            bandBoundary: {
                                isCustomBand: false,
                                bandBoundaryColors
                            },
                            gaugeName,
                            displayLegend
                        }
                    });
                } else if (configMode === 'custom') {
                    this.config.emit({
                        isValid: true,
                        formValues: {
                            businessIndicatorId,
                            year: period,
                            isEstimated,
                            bandBoundary: {
                                isCustomBand: true,
                                bandBoundaryColors: customBands
                            },
                            gaugeName,
                            displayLegend
                        }
                    });
                }
            }
        });
    }

    colorChange(bandBoundaryId: BandBoundaryId, color: ColorPaletteConfiguration) {
        const bandBoundaryColorIndex = this.bandBoundaryColors().findIndex(
            bandBoundaryColor => bandBoundaryColor.bandBoundaryId === bandBoundaryId
        );

        if (bandBoundaryColorIndex > -1) {
            const newBandBoundaryColors = deepCopy(this.bandBoundaryColors());
            newBandBoundaryColors[bandBoundaryColorIndex].color = color;
            this.bandBoundaryColors.set(newBandBoundaryColors);
        }
    }

    updateColor(index: number, color: ColorPaletteConfiguration) {
        const customBandBounds = deepCopy(this.customBandBounds());
        customBandBounds[index].color = color;
        this.customBandBounds.set(customBandBounds);
    }

    updateName(index: number, name: string) {
        const customBandBounds = deepCopy(this.customBandBounds());
        customBandBounds[index].name = name;
        this.customBandBounds.set(customBandBounds);
    }

    updateStart(value: number) {
        const customBandBounds = deepCopy(this.customBandBounds());
        customBandBounds[0].minThreshold = value;

        for (let i = 1; i < customBandBounds.length; i++) {
            if (customBandBounds[i].minThreshold < value) {
                customBandBounds[i].minThreshold = value;
            }
        }
        this.customBandBounds.set(customBandBounds);
    }

    updateThreshold(index: number, value: number) {
        const customBandBounds = deepCopy(this.customBandBounds());

        const computedValue = Math.max(value, index === 0 ? customBandBounds[0].minThreshold : customBandBounds[index - 1].minThreshold);

        customBandBounds[index].minThreshold = computedValue;

        for (let i = index + 1; i < customBandBounds.length; i++) {
            if (customBandBounds[i].minThreshold < computedValue) {
                customBandBounds[i].minThreshold = computedValue;
            }
        }
        this.customBandBounds.set(customBandBounds);
    }

    deleteThreshold(index: number) {
        const customBandBounds = deepCopy(this.customBandBounds());
        customBandBounds.splice(index, 1);

        this.customBandBounds.set(customBandBounds);
    }

    addThreshold() {
        const customBandBounds = deepCopy(this.customBandBounds());
        const length = customBandBounds.length;

        const diffBetweenThreshold = customBandBounds[length - 1].minThreshold - (customBandBounds.at(length - 2)?.minThreshold ?? 0);

        customBandBounds.push({
            name: `${this._translateService.instant('TAILORED_WIDGET.INDICATOR_GAUGE.THRESHOLD')} ${length + 1}`,
            minThreshold: customBandBounds[length - 1].minThreshold + diffBetweenThreshold,
            color: { paletteName: PrettyFeelSharedUiColorName.GRASS, variant: '5' }
        });

        this.customBandBounds.set(customBandBounds);
    }
}
